import { FormStatus, InfoCard, P, Pagination, Skeleton } from '@dnb/eufemia';
import { type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';

interface PagedResponse<T> {
  page: number;
  totalPages: number;
  totalElements: number;
  content: T[];
}

interface PaginatedResultsProps<T> {
  data: PagedResponse<T> | undefined;
  isLoading: boolean;
  error: unknown;
  itemLabel: { singular: string; plural: string };
  renderItems: (items: T[]) => ReactNode;
  renderSkeleton: () => ReactNode;
  emptyMessage?: string;
  errorMessage?: string;
}

export function PaginatedResults<T>({
  data,
  isLoading,
  error,
  itemLabel,
  renderItems,
  renderSkeleton,
  emptyMessage = 'No results match the selected filters.',
  errorMessage = 'Failed to fetch results.',
}: PaginatedResultsProps<T>) {
  const [, setSearchParams] = useSearchParams();

  const items = data?.content ?? [];

  return (
    <Skeleton show={isLoading}>
      <div>
        <P bottom="medium" weight="medium">
          {data?.totalElements ?? 0} {data?.totalElements === 1 ? itemLabel.singular : itemLabel.plural}
        </P>
      </div>
      <div>
        {error ? (
          <FormStatus stretch text={errorMessage} />
        ) : isLoading ? (
          renderSkeleton()
        ) : items.length === 0 ? (
          <InfoCard text={emptyMessage} />
        ) : (
          data && (
            <Pagination
              currentPage={data.page + 1}
              onChange={({ pageNumber }) => {
                setSearchParams((prev) => {
                  prev.set('page', String(pageNumber - 1));
                  return prev;
                });
              }}
              pageCount={data.totalPages}
            >
              {renderItems(items)}
            </Pagination>
          )
        )}
      </div>
    </Skeleton>
  );
}
